import { useRef, useState } from 'react';

import ClearOutlined from '@mui/icons-material/ClearOutlined';
import DragIndicator from '@mui/icons-material/DragIndicator';
import HighlightAltOutlined from '@mui/icons-material/HighlightAltOutlined';
import { Box, Tooltip } from '@mui/material';

import { NameInput } from '@/components/NameInput';
import TooltipIconButton from '@/components/TooltipIconButton';
import { TABNAME_MAX_LENGTH } from '@/features/Dashboard/constants/const';
import { selectDraftTabNames } from '@/features/Dashboard/pages/Edit/model/dashboardEdit.selectors';
import { getTabNameError } from '@/features/Dashboard/pages/Edit/model/utils/invariants';
import { useAppSelector } from '@/hooks/redux-hooks';

import MoveTabToDashboardMenu from './MoveTabToDashboardMenu';

export type EditableTabLabelProps = {
  allowTabReorder: boolean;
  error?: string;
  name: string;
  tabId: string;
  onRename: (tabId: string, value: string) => void;
  onDelete: (tabId: string) => void;
  canDelete: boolean;
  /** Whether there are other dashboards to move to */
  canMove?: boolean;
  /** Ref forwarded to the drag handle element, for use with @dnd-kit sortable */
  dragHandleRef?: React.RefObject<HTMLDivElement | null>;
};

export const EditableTabLabel = ({
  allowTabReorder,
  error: errorProp,
  name,
  tabId,
  onRename,
  onDelete,
  canDelete,
  canMove = true,
  dragHandleRef
}: EditableTabLabelProps) => {
  const [moveMenuOpen, setMoveMenuOpen] = useState(false);
  const moveButtonRef = useRef<HTMLButtonElement>(null);

  const draftTabNames = useAppSelector(selectDraftTabNames);
  const otherNames = draftTabNames.filter(n => n !== name);
  const validationError = getTabNameError(name, otherNames);
  const error = errorProp || validationError;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',

        width: 'calc(100% + 16px)',
        mr: '-8px',
        ml: '-8px'
      }}
    >
      {allowTabReorder && (
        <Tooltip title='Drag to reorder tab'>
          <Box
            ref={dragHandleRef}
            className='tab-drag-handle'
            sx={{
              width: 16,
              height: 48,
              p: 0,
              borderRadius: 0,
              backgroundColor: 'gx.response',

              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'grab'
            }}
          >
            <DragIndicator sx={{ fontSize: '14px' }} />
          </Box>
        </Tooltip>
      )}
      <Box
        data-tab-drag-disabled='true'
        sx={{
          flex: 1,
          maxWidth: '100px',
          width: '100px',
          minWidth: '100px',
          '.MuiInputBase-root': {
            height: '46px'
          }
        }}
      >
        <NameInput
          error={error}
          maxLength={TABNAME_MAX_LENGTH}
          value={name}
          onChange={value => {
            onRename(tabId, value);
          }}
        />
      </Box>
      {canMove && (
        <TooltipIconButton
          ref={moveButtonRef}
          component='div'
          sx={{ height: 46, mr: '2px' }}
          title='Move to ...'
          onClick={e => {
            e.stopPropagation();
            setMoveMenuOpen(true);
          }}
        >
          <HighlightAltOutlined sx={{ fontSize: 18 }} />
        </TooltipIconButton>
      )}
      {canDelete && (
        <TooltipIconButton
          component='div'
          sx={{ height: 46 }}
          title='Remove tab'
          onClick={e => {
            e.stopPropagation();
            onDelete(tabId);
          }}
        >
          <ClearOutlined />
        </TooltipIconButton>
      )}
      <MoveTabToDashboardMenu
        // oxlint-disable-next-line react-hooks-js/refs
        anchorEl={moveButtonRef.current}
        disableMove={!canDelete}
        open={moveMenuOpen}
        tabId={tabId}
        tabName={name}
        onClose={() => {
          setMoveMenuOpen(false);
        }}
        onMoved={() => {
          // The context handles tab removal from tabDrafts
          // and currentTabId fallback
        }}
        onRemoveTab={onDelete}
      />
    </Box>
  );
};
